import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import type { BLPLicenseTypeDistributionDto } from "../../api/getBLPDashboard";

export interface LicenseTypeCount {
  licenseType: string;
  count: number;
}

interface BuslicenseAndPermitDistributionChartProps {
  data: BLPLicenseTypeDistributionDto[];
  isLoading?: boolean;
  isError?: boolean;
}

interface CustomXAxisTickProps {
  x?: number;
  y?: number;
  payload?: {
    value?: string;
  };
  fontSize?: number;
  lineHeight?: number;
  maximumCharactersPerLine?: number;
  maximumLines?: number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  fontSize?: number;
}

interface ResponsiveChartSettings {
  barSize: number;
  barCategoryGap: string;
  chartHeight: number;
  chartTopMargin: number;
  xAxisHeight: number;
  tickFontSize: number;
  tickLineHeight: number;
  maximumCharactersPerLine: number;
  maximumLines: number;
  yAxisFontSize: number;
  tooltipFontSize: number;
}

const getResponsiveChartSettings = (
  containerWidth: number,
): ResponsiveChartSettings => {
  if (containerWidth >= 1200) {
    return {
      barSize: 60,
      barCategoryGap: "16%",
      chartHeight: 340,
      chartTopMargin: 22,
      xAxisHeight: 92,
      tickFontSize: 12,
      tickLineHeight: 16,
      maximumCharactersPerLine: 13,
      maximumLines: 4,
      yAxisFontSize: 12,
      tooltipFontSize: 13,
    };
  }

  if (containerWidth >= 900) {
    return {
      barSize: 52,
      barCategoryGap: "18%",
      chartHeight: 320,
      chartTopMargin: 20,
      xAxisHeight: 88,
      tickFontSize: 11,
      tickLineHeight: 15,
      maximumCharactersPerLine: 12,
      maximumLines: 4,
      yAxisFontSize: 11,
      tooltipFontSize: 12,
    };
  }

  if (containerWidth >= 700) {
    return {
      barSize: 46,
      barCategoryGap: "21%",
      chartHeight: 300,
      chartTopMargin: 18,
      xAxisHeight: 84,
      tickFontSize: 10,
      tickLineHeight: 14,
      maximumCharactersPerLine: 11,
      maximumLines: 4,
      yAxisFontSize: 11,
      tooltipFontSize: 12,
    };
  }

  return {
    barSize: 38,
    barCategoryGap: "24%",
    chartHeight: 285,
    chartTopMargin: 16,
    xAxisHeight: 80,
    tickFontSize: 9,
    tickLineHeight: 13,
    maximumCharactersPerLine: 9,
    maximumLines: 4,
    yAxisFontSize: 10,
    tooltipFontSize: 11,
  };
};

const useResponsiveChartSettings = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [settings, setSettings] = useState<ResponsiveChartSettings>(
    getResponsiveChartSettings(700),
  );

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const updateSettings = (width?: number) => {
      const containerWidth = width ?? container.clientWidth;

      setSettings(getResponsiveChartSettings(containerWidth));
    };

    updateSettings();

    if (typeof ResizeObserver !== "undefined") {
      const resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];

        if (!entry) {
          return;
        }

        updateSettings(entry.contentRect.width);
      });

      resizeObserver.observe(container);

      return () => {
        resizeObserver.disconnect();
      };
    }

    const handleWindowResize = () => {
      updateSettings();
    };

    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  return {
    containerRef,
    settings,
  };
};

const splitLabelIntoLines = (
  value: string,
  maximumCharactersPerLine = 11,
  maximumLines = 4,
): string[] => {
  const words = value.trim().split(/\s+/);

  const lines: string[] = [];
  let currentLine = "";

  words.forEach((word) => {
    const nextLine = currentLine ? `${currentLine} ${word}` : word;

    if (nextLine.length <= maximumCharactersPerLine) {
      currentLine = nextLine;
      return;
    }

    if (currentLine) {
      lines.push(currentLine);
      currentLine = "";
    }

    if (word.length > maximumCharactersPerLine) {
      let remainingWord = word;

      while (remainingWord.length > maximumCharactersPerLine) {
        lines.push(remainingWord.slice(0, maximumCharactersPerLine));

        remainingWord = remainingWord.slice(maximumCharactersPerLine);
      }

      currentLine = remainingWord;
      return;
    }

    currentLine = word;
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  if (lines.length <= maximumLines) {
    return lines;
  }

  const visibleLines = lines.slice(0, maximumLines);

  const lastLineIndex = maximumLines - 1;

  const lastLine = visibleLines[lastLineIndex];

  visibleLines[lastLineIndex] =
    lastLine.length > maximumCharactersPerLine - 3
      ? `${lastLine.slice(0, maximumCharactersPerLine - 3)}...`
      : `${lastLine}...`;

  return visibleLines;
};

const CustomXAxisTick: React.FC<CustomXAxisTickProps> = ({
  x = 0,
  y = 0,
  payload,
  fontSize = 10,
  lineHeight = 14,
  maximumCharactersPerLine = 11,
  maximumLines = 4,
}) => {
  const fullLabel = String(payload?.value ?? "");

  const lines = splitLabelIntoLines(
    fullLabel,
    maximumCharactersPerLine,
    maximumLines,
  );

  return (
    <g transform={`translate(${x},${y})`}>
      <title>{fullLabel}</title>

      {lines.map((line, index) => (
        <text
          key={`${fullLabel}-${index}`}
          x={0}
          y={0}
          dy={12 + index * lineHeight}
          textAnchor="middle"
          fill="#6B7280"
          fontSize={fontSize}
          fontFamily="'Segoe UI', system-ui, sans-serif"
        >
          {line}
        </text>
      ))}
    </g>
  );
};

const CustomTooltip = ({
  active,
  payload,
  fontSize = 12,
}: CustomTooltipProps): React.ReactElement | null => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const item = payload[0].payload as LicenseTypeCount;

  return (
    <div
      className="pointer-events-none rounded-md border border-blue-100 bg-white px-3 py-2 text-center shadow-md"
      style={{
        fontSize,
        fontFamily: "'Segoe UI', system-ui, sans-serif",
      }}
    >
      <p className="font-bold text-black">
        {Number(item.count ?? 0).toLocaleString()}
      </p>

      <p className="font-bold text-[#1E3A6E]">{item.licenseType}</p>
    </div>
  );
};

const CustomLegend = () => (
  <div className="flex items-center justify-center gap-2 font-sans text-xs text-gray-700 2xl:text-sm">
    <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-[#1E3A6E] 2xl:h-3 2xl:w-3" />

    <span>License Type Count</span>
  </div>
);

const BuslicenseAndPermitDistributionChart: React.FC<
  BuslicenseAndPermitDistributionChartProps
> = ({ data, isLoading = false, isError = false }) => {
  const { containerRef, settings } = useResponsiveChartSettings();

  const {
    barSize,
    barCategoryGap,
    chartHeight,
    chartTopMargin,
    xAxisHeight,
    tickFontSize,
    tickLineHeight,
    maximumCharactersPerLine,
    maximumLines,
    yAxisFontSize,
    tooltipFontSize,
  } = settings;

  const chartData: LicenseTypeCount[] = useMemo(() => {
    return data
      .map((item) => ({
        licenseType: item.licenseType?.trim() ?? "",
        count: Number(item.count ?? 0),
      }))
      .filter(
        (item) =>
          item.licenseType !== "" &&
          item.licenseType.toLowerCase() !== "unknown",
      )
      .slice(0, 9);
  }, [data]);

  let chartContent: React.ReactNode;

  if (isLoading) {
    chartContent = (
      <div
        className="flex items-center justify-center text-[13px] text-gray-400"
        style={{
          height: chartHeight,
        }}
      >
        Loading...
      </div>
    );
  } else if (isError) {
    chartContent = (
      <div
        className="flex items-center justify-center text-[13px] text-red-600"
        style={{
          height: chartHeight,
        }}
      >
        Unable to load license distribution data.
      </div>
    );
  } else if (chartData.length === 0) {
    chartContent = (
      <div
        className="flex items-center justify-center text-[13px] text-gray-400"
        style={{
          height: chartHeight,
        }}
      >
        No data available
      </div>
    );
  } else {
    chartContent = (
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          data={chartData}
          margin={{
            top: chartTopMargin,
            right: 16,
            left: 0,
            bottom: 4,
          }}
          barCategoryGap={barCategoryGap}
          barSize={barSize}
        >
          <CartesianGrid vertical={false} stroke="#E5E7EB" />

          <XAxis
            dataKey="licenseType"
            tick={
              <CustomXAxisTick
                fontSize={tickFontSize}
                lineHeight={tickLineHeight}
                maximumCharactersPerLine={maximumCharactersPerLine}
                maximumLines={maximumLines}
              />
            }
            axisLine={false}
            tickLine={false}
            interval={0}
            height={xAxisHeight}
            minTickGap={0}
          />

          <YAxis
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            tick={{
              fontSize: yAxisFontSize,
              fill: "#6B7280",
              fontFamily: "'Segoe UI', system-ui, sans-serif",
            }}
            tickFormatter={(value: number) => value.toLocaleString()}
            width={yAxisFontSize >= 12 ? 50 : 42}
          />

          <Tooltip
            content={<CustomTooltip fontSize={tooltipFontSize} />}
            cursor={false}
          />

          <Bar
            dataKey="count"
            fill="#1E3A6E"
            radius={[6, 6, 0, 0]}
            name="License Type Count"
          />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="shrink-0 rounded-t-xl border-b border-blue-100 bg-blue-50 px-4 py-2">
        <h2 className="m-0 text-left text-sm font-bold text-primary 2xl:text-base">
          Business License Distribution by Type
        </h2>
      </div>

      <div
        ref={containerRef}
        className="flex min-h-0 flex-1 flex-col px-4 pb-3 pt-2"
      >
        <div className="flex min-h-0 flex-1 items-center">
          <div className="w-full">{chartContent}</div>
        </div>

        {!isLoading && !isError && chartData.length > 0 && (
          <div className="shrink-0 border-t border-gray-100 pt-2">
            <CustomLegend />
          </div>
        )}
      </div>
    </div>
  );
};

export default BuslicenseAndPermitDistributionChart;
